import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Shield, Activity, Target, Layers, Loader, Grid, AlertTriangle, Search, ShieldCheck, ClipboardCheck, BarChart2 } from 'lucide-react';
import analysisApi from '../../api/analysisApi';

const VersionSnapshotModal = ({ isOpen, onClose, analysisId, version }) => {
    const { t } = useTranslation();
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('context');

    useEffect(() => {
        if (isOpen && analysisId && version) {
            loadSnapshot();
        } else {
            setSnapshot(null);
        }
    }, [isOpen, analysisId, version]);

    const loadSnapshot = async () => {
        setLoading(true);
        try {
            const response = await analysisApi.getAnalysisSnapshot(analysisId, version.version);
            if (response && response.success) {
                setSnapshot(response.data);
            }
        } catch (error) {
            console.error('Failed to load snapshot', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Use fullAnalysisState if available (new snapshots), otherwise fall back to AnalysisComponent data (old snapshots)
    const fullState = snapshot?.fullAnalysisState || {};
    const componentsData = snapshot?.data || {};

    // --- Data Pre-processing for lookups ---
    const allSources = componentsData['sources-risque']?.sources || [];
    const allDreadedEvents = componentsData['dreaded-events']?.dreadedEvents || [];

    const sourceMap = {};
    allSources.forEach(s => { if (s.id) sourceMap[s.id] = s; });
    const dreadedEventMap = {};
    allDreadedEvents.forEach(de => { if (de.id) dreadedEventMap[de.id] = de; });

    // Build a human-readable label from a scenario object
    // Covers: name, sourceRisque+dreadedEvent combo, referenceCode, id
    const buildScenarioLabel = (s) => {
        if (!s) return '—';
        if (s.name && s.name.trim()) return s.name.trim();

        // Try to resolve names from maps if only IDs are provided
        const srId = s.sourceRisqueId || s.sourceRisque || s.riskSourceId || s.sourceId;
        const deId = s.dreadedEventId || s.dreadedEvent || s.dreadedEventName || s.referenceEvent;

        const sr = sourceMap[srId];
        const de = dreadedEventMap[deId];

        if (sr && de) return `${sr.name} → ${de.name}`;
        if (sr) return sr.name;
        if (de) return de.name;

        // Fallback to explicit fields
        const srName = s.sourceRisqueName || s.riskSourceName;
        const deName = s.dreadedEventName || s.referenceEventName;
        if (srName && deName) return `${srName} → ${deName}`;

        // Try reference code
        if (s.referenceCode) return s.referenceCode;
        // Try description snippet
        if (s.description) return s.description.slice(0, 60) + (s.description.length > 60 ? '…' : '');
        // Last resort: id
        return s.id || s._id || '—';
    };

    // Helper to get data from either source
    const getContext = () => fullState.context || componentsData.context || {};
    const getAtelier3 = () => fullState.atelier3 || {};
    const getAiSettings = () => fullState.aiSettings || {};

    // Helper to get rule details from frameworks in snapshot
    // Searches both frameworks[].rules[] (standard) and selectedRules (custom frameworks)
    const getRuleDetails = (ruleId) => {
        const sfData = componentsData['security-framework'] || {};
        const frameworks = sfData.frameworks || [];

        // 1. Search in standard frameworks
        for (const fw of frameworks) {
            if (fw.rules) {
                const rule = fw.rules.find(r => r.id === ruleId || r.originalId === ruleId || r.code === ruleId);
                if (rule) return { ...rule, frameworkName: fw.name, code: rule.code || rule.originalId || ruleId };
            }
        }

        // 2. Search in selectedRules (custom frameworks stored as Map or plain object)
        const selectedRules = sfData.selectedRules || {};
        const allSelectedRules = Array.isArray(selectedRules)
            ? selectedRules
            : Object.values(selectedRules).flat();
        const selectedRule = allSelectedRules.find(r => r && (r.id === ruleId || r.originalId === ruleId || r.code === ruleId));
        if (selectedRule) {
            return {
                ...selectedRule,
                name: selectedRule.name || selectedRule.title || ruleId,
                code: selectedRule.code || selectedRule.originalId || ruleId,
                frameworkName: selectedRule.frameworkName || selectedRule.framework || 'Référentiel personnalisé'
            };
        }

        return { name: ruleId, description: null, code: ruleId, frameworkName: 'Unknown' };
    };

    // Tab Content Renderers
    const renderContext = () => {
        const context = fullState; // fullState has name, description, scope, etc. directly
        const contextComp = getContext(); // AnalysisComponent data for legacy support

        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
            >
                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-slate-500 mb-4 uppercase">{t('versionManagement.snapshot.context.generalInfo')}</h3>
                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6 text-sm">
                        <div className="col-span-2 md:col-span-4">
                            <dt className="text-slate-500 text-xs mb-1">{t('versionManagement.snapshot.context.descObjectives')}</dt>
                            <dd className="font-medium text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
                                {fullState.description || '-'}
                            </dd>
                        </div>
                        <div className="col-span-2">
                            <dt className="text-slate-500 text-xs mb-1">{t('versionManagement.snapshot.context.scope')}</dt>
                            <dd className="font-medium text-slate-900 dark:text-slate-100 bg-blue-50/50 dark:bg-blue-900/10 p-3 rounded border border-blue-100 dark:border-blue-900/30">
                                {contextComp.scope || '-'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-500 text-xs mb-1">{t('versionManagement.snapshot.context.orgName')}</dt>
                            <dd className="font-medium text-slate-900 dark:text-slate-100">{contextComp.organizationName || context.companyName || '-'}</dd>
                        </div>
                        <div>
                            <dt className="text-slate-500 text-xs mb-1">{t('versionManagement.snapshot.context.analysisDate')}</dt>
                            <dd className="font-medium text-slate-900 dark:text-slate-100">
                                {contextComp.analysisDate ? new Date(contextComp.analysisDate).toLocaleDateString() : '-'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-500 text-xs mb-1">{t('versionManagement.snapshot.context.manager')}</dt>
                            <dd className="font-medium text-slate-900 dark:text-slate-100">{context.createdByName || '-'}</dd>
                        </div>
                        <div>
                            <dt className="text-slate-500 text-xs mb-1">{t('versionManagement.snapshot.context.classification')}</dt>
                            <dd className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                {context.classification || 'Confidentielle'}
                            </dd>
                        </div>
                    </dl>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Hypothèses */}
                    <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase flex items-center gap-2">
                            <Target className="w-4 h-4 text-blue-500" /> {t('versionManagement.snapshot.context.hypotheses')}
                        </h3>
                        <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg min-h-[100px] border border-slate-100 dark:border-slate-800">
                            {contextComp.hypotheses?.length > 0 ? (
                                <ul className="space-y-2">
                                    {contextComp.hypotheses.map((h, i) => (
                                        <li key={i} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                                            <span className="text-blue-400 mt-1">•</span>
                                            <span>{h}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-slate-400 italic text-sm">{t('versionManagement.snapshot.context.noHypotheses')}</p>
                            )}
                        </div>
                    </div>

                    {/* Contraintes */}
                    <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase flex items-center gap-2">
                            <Activity className="w-4 h-4 text-orange-500" /> {t('versionManagement.snapshot.context.constraints')}
                        </h3>
                        <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg min-h-[100px] border border-slate-100 dark:border-slate-800">
                            {contextComp.contraintes?.length > 0 ? (
                                <ul className="space-y-2">
                                    {contextComp.contraintes.map((c, i) => (
                                        <li key={i} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                                            <span className="text-orange-400 mt-1">•</span>
                                            <span>{c}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-slate-400 italic text-sm">{t('versionManagement.snapshot.context.noConstraints')}</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase flex items-center gap-2">
                            <FileText className="w-4 h-4 text-indigo-500" /> {t('versionManagement.snapshot.context.missions')}
                        </h3>
                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg min-h-[100px] border border-slate-100 dark:border-slate-800">
                            {contextComp.missions?.length > 0
                                ? contextComp.missions.map((m, i) => <li key={i} className="pl-1">{m}</li>)
                                : <li className="text-slate-400 italic list-none">{t('versionManagement.snapshot.context.noMissions')}</li>}
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase flex items-center gap-2">
                            <Layers className="w-4 h-4 text-purple-500" /> {t('versionManagement.snapshot.context.participantsRaci')}
                        </h3>
                        <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg min-h-[100px] border border-slate-100 dark:border-slate-800">
                            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                                {contextComp.participants?.length > 0
                                    ? contextComp.participants.map((p, i) => (
                                        <li key={i} className="flex flex-wrap items-center gap-2">
                                            <span className="font-medium text-slate-900 dark:text-slate-100">{p.name}</span>
                                            {p.role && <span className="text-xs text-slate-500"> — {p.role}</span>}
                                            {contextComp.matrix?.[p.id] && (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded font-bold uppercase">
                                                    {contextComp.matrix[p.id]}
                                                </span>
                                            )}
                                        </li>
                                    ))
                                    : <li className="text-slate-400 italic">{t('versionManagement.snapshot.context.noParticipants')}</li>}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Responsible Persons for Control Measures */}
                <div>
                    <h3 className="text-sm font-semibold text-slate-500 mb-2 uppercase flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-500" /> Responsables des Mesures de Contrôle
                    </h3>
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                        {contextComp.responsiblePersonsList?.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {contextComp.responsiblePersonsList.map((r, i) => (
                                    <div key={i} className="bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-700 flex items-center gap-2 shadow-sm">
                                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-xs">
                                            {r.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{r.name}</div>
                                            {r.role && <div className="text-[10px] text-slate-500 truncate">{r.role}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-400 italic text-sm">Aucun responsable spécifique défini pour les mesures</p>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    const renderSocle = () => {
        const atelier3 = getAtelier3();
        const businessValues = componentsData['business-values']?.businessValues || [];
        const dreadedEvents = componentsData['dreaded-events']?.dreadedEvents || [];
        // Stakeholders: check dedicated component first, then atelier3 on Analysis doc
        const stakeholders = componentsData['stakeholders']?.stakeholders || atelier3.stakeholders || [];
        const securityRules = componentsData['security-framework']?.selectedRules || {};

        // Count total rules by iterating all frameworks in selectedRules
        let rulesCount = 0;
        let ruleItems = [];

        Object.entries(securityRules).forEach(([fwId, rules]) => {
            if (Array.isArray(rules)) {
                rulesCount += rules.length;
                ruleItems.push(...rules);
            }
        });

        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-8"
            >
                {/* Business Values */}
                <div>
                    <h3 className="flex items-center gap-2 text-blue-700 dark:text-blue-400 font-semibold mb-3 border-b border-blue-100 dark:border-blue-900 pb-2">
                        <Shield className="w-5 h-5" /> Valeurs Métier ({businessValues.length})
                    </h3>
                    <div className="space-y-2">
                        {businessValues.length > 0 ? (
                            businessValues.map((bv, i) => (
                                <div key={i} className="p-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/50 rounded-lg">
                                    <div className="font-medium text-slate-800 dark:text-slate-200">{bv.name}</div>
                                    {bv.description && <div className="text-xs text-slate-500 mt-1">{bv.description}</div>}
                                    {bv.supportAssets?.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {bv.supportAssets.map((sa, j) => (
                                                <span key={j} className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded">
                                                    🖥️ {typeof sa === 'string' ? sa : sa.name || sa}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2">Aucune valeur métier identifiée</div>
                        )}
                    </div>
                </div>

                {/* Dreaded Events */}
                <div>
                    <h3 className="flex items-center gap-2 text-red-700 dark:text-red-400 font-semibold mb-3 border-b border-red-100 dark:border-red-900 pb-2">
                        <AlertTriangle className="w-5 h-5" /> Événements Redoutés ({dreadedEvents.length})
                    </h3>
                    <div className="space-y-2">
                        {dreadedEvents.length > 0 ? (
                            dreadedEvents.map((de, i) => (
                                <div key={i} className="p-3 bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/50 rounded-lg">
                                    <div className="font-medium text-slate-800 dark:text-slate-200">{de.name}</div>
                                    {de.description && <div className="text-xs text-slate-500 mt-1">{de.description}</div>}
                                    <div className="mt-2 flex gap-2">
                                        {de.impacts?.map((imp, j) => (
                                            <span key={j} className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded uppercase font-bold tracking-wider">
                                                {typeof imp === 'string' ? imp : imp.impact || String(imp)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2">Aucun événement redouté</div>
                        )}
                    </div>
                </div>



                {/* Security Baseline */}
                <div>
                    <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase border-b border-slate-100 dark:border-slate-800 pb-2 mt-6">
                        Socle de Sécurité
                    </h3>
                    <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Règles sélectionnées</span>
                            <span className="text-sm font-bold bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded text-slate-700 dark:text-slate-300">{rulesCount}</span>
                        </div>
                        {rulesCount > 0 && (
                            <div className="mt-3 text-xs text-slate-500 max-h-64 overflow-y-auto space-y-1">
                                {ruleItems.map((rule, i) => (
                                    <div key={i} className="truncate">• {rule.name || rule.id} <span className='text-slate-400'>({rule.id})</span></div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    const renderScenarios = () => {
        // Atelier 3 — Parties Prenantes
        const atelier3 = getAtelier3();
        const stakeholders = componentsData['stakeholders']?.stakeholders || atelier3.stakeholders || [];

        // === HYDRATE Strategic Scenarios from base data (mappings × sources × events) ===
        // This mirrors the backend scenarioHydrationService logic
        const mappings = componentsData['selected-couples-srov']?.mappings || [];
        const businessValues = componentsData['business-values']?.businessValues || [];

        let strategicScenarios = [];

        if (mappings.length > 0 && allSources.length > 0 && allDreadedEvents.length > 0) {
            // Hydrate from mappings (the proper approach)
            strategicScenarios = mappings.map((mapping, index) => {
                const source = sourceMap[mapping.sourceId];
                const event = dreadedEventMap[mapping.dreadedEventId];
                if (!source || !event) return null;

                const businessValue = businessValues.find(bv => String(bv.id) === String(event.businessValue));

                // Calculate niveau (risk level) from source attributes
                const calcScore = (val) => {
                    const norm = String(val || '').toLowerCase();
                    return norm.startsWith('elev') ? 3 : norm.startsWith('moyen') ? 2 : 1;
                };
                const totalScore = calcScore(source.motivation) + calcScore(source.activite) + calcScore(source.ressources);
                const niveau = totalScore < 6 ? 1 : totalScore === 6 ? 2 : totalScore <= 7 ? 3 : 4;

                return {
                    id: mapping.id || `${mapping.sourceId}-${mapping.dreadedEventId}`,
                    scenarioNumber: index + 1,
                    referenceCode: `S${String(index + 1).padStart(2, '0')}`,
                    name: `${source.name} → ${event.name}`,
                    sourceRiskName: source.name,
                    sourceCategory: source.category,
                    dreadedEventName: event.name,
                    severity: event.severity,
                    securityPillar: event.securityPillar,
                    businessValueName: businessValue?.name || null,
                    niveau: niveau,
                    motivation: source.motivation,
                    activite: source.activite,
                    ressources: source.ressources
                };
            }).filter(Boolean);
        }

        // Fallback: try pre-saved strategic-scenarios component or workshop5-data
        if (strategicScenarios.length === 0) {
            strategicScenarios = componentsData['strategic-scenarios']?.strategicScenarios
                || componentsData['strategic-scenarios']?.scenarios
                || (Array.isArray(componentsData['strategic-scenarios']) ? componentsData['strategic-scenarios'] : null)
                || componentsData['workshop5-data']?.strategicScenarios
                || componentsData['atelier5-synthesis']?.strategicScenarios
                || [];
        }

        // Atelier 4 — Operational Scenarios (CTI)
        const operationalScenarios = componentsData['cti-operational-scenarios']?.scenarios || [];

        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-8"
            >
                {/* Atelier 3 — Parties Prenantes */}
                <div>
                    <h3 className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400 font-semibold mb-3 border-b border-indigo-100 dark:border-indigo-900 pb-2">
                        <Layers className="w-5 h-5" /> Parties Prenantes — Atelier 3 ({stakeholders.length})
                    </h3>
                    <div className="space-y-2">
                        {stakeholders.length > 0 ? (
                            stakeholders.map((sh, i) => (
                                <div key={i} className="p-3 bg-indigo-50/50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/50 rounded-lg flex justify-between items-center">
                                    <div>
                                        <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{sh.name}</div>
                                        <div className="text-xs text-slate-500">{sh.type}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        {sh.category && (
                                            <span className="text-[10px] px-2 py-0.5 bg-white dark:bg-slate-800 rounded border border-indigo-100 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 font-medium">
                                                {sh.category}
                                            </span>
                                        )}
                                        {sh.securityLevel && (
                                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded">
                                                Niveau: {sh.securityLevel}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2">Aucune partie prenante identifiée</div>
                        )}
                    </div>
                </div>

                {/* Atelier 3 — Scénarios Stratégiques */}
                <div>
                    <h3 className="flex items-center gap-2 text-purple-700 dark:text-purple-400 font-semibold mb-3 border-b border-purple-100 dark:border-purple-900 pb-2">
                        <Target className="w-5 h-5" /> Scénarios Stratégiques — Atelier 3 ({strategicScenarios.length})
                    </h3>
                    <div className="space-y-3">
                        {strategicScenarios.length > 0 ? (
                            strategicScenarios.map((s, i) => {
                                const niveauColors = {
                                    1: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
                                    2: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
                                    3: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
                                    4: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                };
                                const niveauLabels = { 1: 'Faible', 2: 'Moyen', 3: 'Élevé', 4: 'Critique' };
                                const niv = s.niveau || 0;

                                return (
                                    <div key={i} className="p-4 bg-purple-50/30 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-xl space-y-2">
                                        {/* Header: Reference + Name */}
                                        <div className="flex items-start gap-3">
                                            <span className="text-[10px] font-mono font-bold px-2 py-1 bg-purple-200/60 dark:bg-purple-800/40 text-purple-700 dark:text-purple-300 rounded-md shrink-0">
                                                {s.referenceCode || `S${String(i + 1).padStart(2, '0')}`}
                                            </span>
                                            <div className="font-bold text-slate-800 dark:text-slate-100">
                                                {s.name || buildScenarioLabel(s)}
                                            </div>
                                        </div>

                                        {/* Badges row */}
                                        <div className="flex flex-wrap gap-2 text-[11px] pl-1">
                                            {/* Niveau */}
                                            {niv > 0 && (
                                                <span className={`px-2 py-0.5 rounded-full font-semibold ${niveauColors[niv] || ''}`}>
                                                    Niveau: {niveauLabels[niv] || niv}
                                                </span>
                                            )}
                                            {/* Severity */}
                                            {s.severity && (
                                                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full font-medium">
                                                    Gravité: <span className="font-bold">{s.severity}</span>
                                                </span>
                                            )}
                                            {/* Security Pillar */}
                                            {s.securityPillar && (
                                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-medium">
                                                    {s.securityPillar}
                                                </span>
                                            )}
                                            {/* Source Category */}
                                            {s.sourceCategory && (
                                                <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full font-medium">
                                                    Source: {s.sourceCategory}
                                                </span>
                                            )}
                                        </div>

                                        {/* Business Value */}
                                        {s.businessValueName && (
                                            <div className="text-xs text-slate-600 dark:text-slate-400 pl-1">
                                                Valeur métier: <span className="font-medium text-slate-700 dark:text-slate-300">{s.businessValueName}</span>
                                            </div>
                                        )}

                                        {/* Description fallback */}
                                        {s.description && (
                                            <div className="text-xs text-slate-600 dark:text-slate-400 bg-white/50 dark:bg-slate-800/50 p-3 rounded-lg border border-purple-50 dark:border-purple-900/20 leading-relaxed italic">
                                                {s.description}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2">Aucun scénario stratégique dans ce snapshot.</div>
                        )}
                    </div>
                </div>

                {/* Atelier 4 — Scénarios Opérationnels CTI */}
                <div>
                    <h3 className="flex items-center gap-2 text-orange-700 dark:text-orange-400 font-semibold mb-3 border-b border-orange-100 dark:border-orange-900 pb-2">
                        <Activity className="w-5 h-5" /> Scénarios Opérationnels — Atelier 4 CTI ({operationalScenarios.length})
                    </h3>
                    <div className="space-y-2">
                        {operationalScenarios.length > 0 ? (
                            operationalScenarios.map((s, i) => (
                                <div key={i} className="p-3 bg-orange-50/50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/50 rounded-lg text-sm">
                                    <div className="font-medium text-slate-800 dark:text-slate-200 mb-1">{s.name}</div>
                                    <div className="flex gap-2 text-xs text-slate-500">
                                        {s.severity && <span>⚡ Gravité: <span className="font-semibold">{s.severity}</span></span>}
                                        {s.likelihood && <span>📊 Vraisemblance: <span className="font-semibold">{s.likelihood}</span></span>}
                                        {s.attackPathId && <span className="text-slate-400 italic">Chemin: {s.attackPathId}</span>}
                                    </div>
                                    {s.description && <div className="text-xs text-slate-400 mt-1 line-clamp-2">{s.description}</div>}
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2">Aucun scénario opérationnel CTI</div>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    const renderSources = () => {
        const sources = componentsData['sources-risque']?.sources || [];
        const dreadedEvents = componentsData['dreaded-events']?.dreadedEvents || [];

        // Atelier 2 — Objectifs Visés — backend key is `data.objectifs`
        const objectifs = componentsData['objectifs-vises']?.objectifs
            || componentsData['objectifs-vises']?.objectives
            || componentsData['objectives']?.objectifs
            || componentsData['objectives']?.objectives
            || [];

        // Atelier 2 — Couples SR × ER (Source × Dreaded Event)
        const couples = componentsData['selected-couples-srov']?.mappings
            || componentsData['selected-couples-srov']?.selectedCouples
            || [];

        // Build lookup maps for name resolution
        const sourceMap = {};
        sources.forEach(s => { if (s.id) sourceMap[s.id] = s; });
        const dreadedEventMap = {};
        dreadedEvents.forEach(de => { if (de.id) dreadedEventMap[de.id] = de; });

        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-8"
            >
                {/* Sources de Risque */}
                <div>
                    <h3 className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-semibold mb-3 border-b border-amber-100 dark:border-amber-900 pb-2">
                        <Search className="w-5 h-5" /> Sources de Risque ({sources.length})
                    </h3>
                    <div className="space-y-2">
                        {sources.length > 0 ? (
                            sources.map((s, i) => (
                                <div key={i} className="p-3 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/50 rounded-lg">
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="font-medium text-slate-800 dark:text-slate-200 flex-1">{s.name}</div>
                                        <div className="flex gap-1 shrink-0">
                                            {s.selected && (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-bold uppercase">Retenu</span>
                                            )}
                                            <span className="text-xs px-2 py-1 bg-white dark:bg-slate-800 rounded border border-amber-100 dark:border-amber-800 text-amber-600 dark:text-amber-400">
                                                {s.category || s.type || 'Non classé'}
                                            </span>
                                        </div>
                                    </div>
                                    {s.objectifVise && (
                                        <div className="text-xs text-slate-500 mt-1">
                                            <span className="font-semibold">Objectif visé:</span> {s.objectifVise}
                                            {s.objectifViseCategory && <span className="ml-1 text-[10px] px-1 py-0.5 bg-orange-100 text-orange-700 rounded">{s.objectifViseCategory}</span>}
                                        </div>
                                    )}
                                    {(s.motivation || s.activite || s.ressources) && (
                                        <div className="flex gap-3 text-[10px] text-slate-500 mt-1.5">
                                            {s.motivation && <span>Motivation: <span className="font-medium">{s.motivation}</span></span>}
                                            {s.activite && <span>Activité: <span className="font-medium">{s.activite}</span></span>}
                                            {s.ressources && <span>Ressources: <span className="font-medium">{s.ressources}</span></span>}
                                        </div>
                                    )}
                                    {s.description && <div className="text-xs text-slate-500 mt-1 italic line-clamp-2">{s.description}</div>}
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2">Aucune source de risque identifiée</div>
                        )}
                    </div>
                </div>


                {/* Couples SR × ER */}
                <div>
                    <h3 className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-semibold mb-3 border-b border-rose-100 dark:border-rose-900 pb-2">
                        <span className="text-base">🔗</span> Couples SR × ER retenus ({couples.length})
                    </h3>
                    {couples.length > 0 ? (
                        <div className="space-y-2">
                            {couples.map((c, i) => {
                                // Resolve names from lookup maps; fall back gracefully
                                const sr = sourceMap[c.sourceId] || sourceMap[c.sourceRisqueId];
                                const er = dreadedEventMap[c.dreadedEventId] || dreadedEventMap[c.dreadedEventName];
                                const srName = c.sourceName || c.sourceRisqueName || sr?.name || c.sourceId || c.id || '—';
                                const erName = c.dreadedEventName || er?.name || c.objectifName || c.dreadedEventId || '—';
                                return (
                                    <div key={i} className="p-3 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/50 rounded-lg text-sm">
                                        <div className="flex items-start gap-2 flex-wrap">
                                            <span className="font-medium text-slate-800 dark:text-slate-200 flex-1 min-w-0">
                                                <span className="text-[10px] uppercase font-bold text-amber-600 mr-1">SR</span>
                                                {srName}
                                            </span>
                                            <span className="text-slate-300 font-bold text-lg leading-none self-center">→</span>
                                            <span className="font-medium text-slate-800 dark:text-slate-200 flex-1 min-w-0">
                                                <span className="text-[10px] uppercase font-bold text-red-500 mr-1">ER</span>
                                                {erName}
                                            </span>
                                        </div>
                                        {(sr?.objectifVise || sr?.category) && (
                                            <div className="mt-1.5 flex gap-2 text-[10px] text-slate-500">
                                                {sr?.objectifVise && <span>🎯 {sr.objectifVise}</span>}
                                                {sr?.category && <span className="px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">{sr.category}</span>}
                                            </div>
                                        )}
                                        {c.justification && <div className="text-xs text-slate-500 mt-1 italic border-l-2 border-rose-200 pl-2">{c.justification}</div>}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-slate-400 italic text-sm pl-2">Aucun couple SR×ER sélectionné</div>
                    )}
                </div>
            </motion.div>
        );
    };

    const renderTreatment = () => {
        // Try getting treatment from multiple sources:
        // 1. Dedicated risk-treatment component
        // 2. Workshop5Data treatment strategies
        // 3. Atelier5 synthesis
        const riskTreatmentComp = componentsData['risk-treatment']?.riskTreatments || [];

        // Attempt 2: w5 data embedded treatments
        const w5Scenarios = componentsData['workshop5-data']?.strategicScenarios || [];
        const a5Scenarios = componentsData['atelier5-synthesis']?.strategicScenarios || [];

        // Use the richest source for scenarios
        const scenarioPool = a5Scenarios.length > 0 ? a5Scenarios : w5Scenarios;

        // Build treatments from scenario pool — riskTreatment can be { option, justification } or { decision, comment }
        const scenarioTreatments = scenarioPool
            .filter(s => s.riskTreatment && (s.riskTreatment.option || s.riskTreatment.decision))
            .map(s => ({
                scenarioId: s.id || s.scenarioId,
                scenarioName: buildScenarioLabel(s),
                _scenario: s,   // keep original for gravity/likelihood
                decision: s.riskTreatment.option || s.riskTreatment.decision || '-',
                comment: s.riskTreatment.justification || s.riskTreatment.comment || s.riskTreatment.details || ''
            }));

        // Merge all treatment sources — prefer dedicated comp, then scenario pool
        let treatments = riskTreatmentComp.length > 0
            ? riskTreatmentComp
            : scenarioTreatments;

        // Helper to get scenario details robustly
        const getScenario = (id) => {
            const sources = [
                componentsData['strategic-scenarios']?.strategicScenarios,
                componentsData['strategic-scenarios']?.scenarios,
                Array.isArray(componentsData['strategic-scenarios']) ? componentsData['strategic-scenarios'] : null,
                componentsData['workshop5-data']?.strategicScenarios,
                componentsData['atelier5-synthesis']?.strategicScenarios
            ];

            for (const src of sources) {
                if (!src) continue;
                const found = Array.isArray(src) ? src.find(s => s.id === id || s._id === id) : null;
                if (found) return found;
            }
            return null;
        };

        const getScenarioName = (id) => {
            const s = getScenario(id);
            return s?.name || s?.sourceRisque || id;
        };

        const strategyLabels = {
            reduce: 'Réduire',
            reduce_risk: 'Réduire',
            accept: 'Accepter',
            accept_risk: 'Accepter',
            avoid: 'Éviter',
            avoid_risk: 'Éviter',
            transfer: 'Transférer',
            transfer_risk: 'Transférer'
        };

        const getStrategyColor = (s) => {
            const strategy = s?.toLowerCase() || '';
            if (strategy.includes('reduce')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
            if (strategy.includes('accept')) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
            if (strategy.includes('avoid')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
            if (strategy.includes('transfer')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
            return 'bg-slate-100 text-slate-500';
        };

        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
            >
                <div>
                    <h3 className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold mb-3 border-b border-emerald-100 dark:border-emerald-900 pb-2">
                        <ClipboardCheck className="w-5 h-5" /> Traitement du Risque ({treatments.length})
                    </h3>
                    <div className="space-y-4">
                        {treatments.length > 0 ? (
                            treatments.map((t, i) => {
                                // _scenario is set when built from the pool, else fallback to getScenario()
                                const scenario = t._scenario || getScenario(t.scenarioId);
                                const scenarioName = t.scenarioName || buildScenarioLabel(scenario) || t.scenarioId;
                                const gravity = scenario?.gravity || scenario?.gravite;
                                const likelihood = scenario?.likelihood || scenario?.vraisemblance;

                                return (
                                    <div key={i} className="p-4 bg-emerald-50/30 dark:bg-emerald-900/5 border border-emerald-100 dark:border-emerald-900/30 rounded-xl">
                                        <div className="flex justify-between items-start gap-4 mb-3">
                                            <div className="flex-1">
                                                <div className="font-bold text-slate-800 dark:text-slate-100 mb-1">
                                                    {scenarioName}
                                                </div>
                                                {scenario?.description && (
                                                    <div className="text-xs text-slate-500 line-clamp-1 italic mb-1">
                                                        {scenario.description}
                                                    </div>
                                                )}
                                                {(gravity || likelihood) && (
                                                    <div className="flex gap-2 mt-1">
                                                        {gravity && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded border border-slate-200 dark:border-slate-700">Gravité: <span className="font-bold">{gravity}</span></span>}
                                                        {likelihood && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded border border-slate-200 dark:border-slate-700">Vraisemblance: <span className="font-bold">{likelihood}</span></span>}
                                                    </div>
                                                )}
                                            </div>
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider shrink-0 ${getStrategyColor(t.decision)} shadow-sm`}>
                                                {strategyLabels[t.decision?.toLowerCase()] || t.decision || 'N/D'}
                                            </span>
                                        </div>

                                        <div className="bg-white/60 dark:bg-slate-900/40 p-2.5 rounded-lg border border-emerald-100/50 dark:border-emerald-900/20">
                                            <div className="text-[10px] uppercase font-bold text-emerald-600 dark:text-emerald-500 mb-1">Commentaire / Justification</div>
                                            <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                                                {t.comment || <span className="text-slate-400 italic">Aucun commentaire de justification saisi.</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-slate-400 italic text-sm pl-2 py-4">Aucun traitement de risque défini</div>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    };

    const renderControls = () => {
        // Data Sources:
        // 1. Security Assurance Plan (analysis-control) — rule checklist
        const planData = componentsData['analysis-control'] || {};
        const plan = planData.plan || {};

        // Get all selected rules to ensure consistency with Socle tab
        const securityRules = componentsData['security-framework']?.selectedRules || {};
        const allSelectedRulesList = [];
        Object.entries(securityRules).forEach(([fwId, rules]) => {
            if (Array.isArray(rules)) {
                allSelectedRulesList.push(...rules);
            }
        });

        // 2. Workshop 5 synthesis — three types of measures per strategic scenario
        const w5Data = componentsData['atelier5-synthesis'] || {};
        const w5Scenarios = w5Data.strategicScenarios || [];

        // ── Name resolution helpers for dehydrated measures ──────────────────
        // Build a lookup map: controlId → control name from analysis-control plan
        const controlNameMap = {};
        Object.values(plan).forEach(entry => {
            (entry.controls || []).forEach(ctrl => {
                if (ctrl.controlId && ctrl.name) controlNameMap[ctrl.controlId] = ctrl;
                if (ctrl.id && ctrl.name) controlNameMap[ctrl.id] = ctrl;
            });
        });

        // Build a lookup map: measure id → measure from ecosystem-measures component
        const ecosystemMeasureMap = {};
        const ecoMeasures = componentsData['ecosystem-measures']?.measures || [];
        ecoMeasures.forEach(m => {
            if (m.id) ecosystemMeasureMap[m.id] = m;
        });

        // Hydrate a dehydrated dreadedEventMeasure reference with its name/description
        const hydrateDreadedMeasure = (ref) => {
            const ctrl = controlNameMap[ref.id];
            if (ctrl) return { ...ref, name: ctrl.name, description: ctrl.description, responsiblePerson: ctrl.responsiblePerson, category: ctrl.measure_type || ctrl.category };
            return ref;
        };

        // Hydrate a dehydrated ecosystemMeasure reference with its name/description
        const hydrateEcosystemMeasure = (ref) => {
            const m = ecosystemMeasureMap[ref.id];
            if (m) return { ...ref, name: m.title || m.name, description: m.description, category: m.category, priority: m.priority, implementation: m.implementation };
            return ref;
        };

        // ── Unify all measures grouped by scenario ───────────────────────────
        // All measures come from atelier5-synthesis → strategicScenarios[].{dreadedEventMeasures, ecosystemMeasures, customMeasures}
        // dreadedEventMeasures → references to analysis-control controls (hydrated via controlNameMap)
        // ecosystemMeasures   → references to ecosystem-measures (hydrated via ecosystemMeasureMap)
        // customMeasures      → full inline objects from Atelier 5
        const scenarioMeasureGroups = []; // [{ scenarioName, measures: [{...measure, _type}] }]

        w5Scenarios.forEach(scenario => {
            const sName = scenario.name || scenario.id;
            const measures = [];

            (scenario.dreadedEventMeasures || []).forEach(m => {
                const hydrated = hydrateDreadedMeasure(m);
                measures.push({ ...hydrated, _type: 'ER', _scenarioName: sName });
            });
            (scenario.ecosystemMeasures || []).forEach(m => {
                const hydrated = hydrateEcosystemMeasure(m);
                measures.push({ ...hydrated, _type: 'ECO', _scenarioName: sName });
            });
            (scenario.customMeasures || []).forEach(m => {
                measures.push({ ...m, _type: 'CUSTOM', _scenarioName: sName });
            });

            if (measures.length > 0) {
                scenarioMeasureGroups.push({ scenarioName: sName, measures });
            }
        });

        const totalMeasures = scenarioMeasureGroups.reduce((s, g) => s + g.measures.length, 0);

        const priorityBadge = (p) => ({ high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', critical: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' }[p] || 'bg-slate-100 text-slate-500');
        const implBadge = (s) => ({ applied: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', planned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', 'in-progress': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' }[s] || 'bg-slate-100 text-slate-500');

        const getMeasureName = (m) =>
            m.name || m.title || m.ruleName || m.measureName || m.dreadedEventName || m.stakeholderName ||
            (m.ruleId ? `Règle ${m.ruleId}` : null) || (m.id ? `Mesure ${m.id.slice(-6)}` : 'Mesure sans nom');

        // Compact card: title + badges on one line, description below, meta as text separated by " · "
        const MeasureCard = ({ measure, borderColor, typeBadge }) => {
            const name = getMeasureName(measure);
            const impl = measure.implementation || measure.status;
            const metaParts = [
                measure.responsiblePerson && `${measure.responsiblePerson}`,
                measure.category && `${measure.category}`,
                measure.temporalPosition && (measure.temporalPosition === 'immediate' ? '0 mois' : measure.temporalPosition === 'shortTerm' ? '+3 mois' : '+6 mois'),
            ].filter(Boolean);

            return (
                <div className={`pl-3 pr-3 pt-2.5 pb-2 rounded-lg border-l-2 ${borderColor} bg-white dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800 space-y-1`}>
                    {/* Row 1: name + badges */}
                    <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 flex-1 leading-snug">{name}</div>
                        <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                            {typeBadge && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${typeBadge.cls}`}>{typeBadge.label}</span>}
                            {measure.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${priorityBadge(measure.priority)}`}>{measure.priority}</span>}
                            {impl && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${implBadge(impl)}`}>{impl}</span>}
                        </div>
                    </div>
                    {/* Row 2: description */}
                    {measure.description && <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{measure.description}</div>}
                    {/* Row 3: meta inline (no scenario name — shown as group header) */}
                    {metaParts.length > 0 && (
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {metaParts.map((p, i) => <span key={i}>{p}</span>).reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`s${i}`} className="opacity-40">·</span>, el], [])}
                        </div>
                    )}
                </div>
            );
        };

        // Section block with a clean header (no emojis)
        const SectionBlock = ({ title, count, colorText, colorBorder, children, empty }) => (
            <div>
                <div className={`flex items-baseline justify-between pb-1.5 mb-3 border-b ${colorBorder}`}>
                    <h4 className={`font-semibold text-sm ${colorText}`}>{title}</h4>
                    <span className="text-xs font-bold text-slate-400 tabular-nums">{count}</span>
                </div>
                {count > 0 ? <div className="space-y-2">{children}</div> : <p className="text-slate-400 italic text-xs pl-1">{empty}</p>}
            </div>
        );

        const decisionBadge = (d) => ({
            apply: 'bg-green-100 text-green-700',
            applied: 'bg-green-100 text-green-700',
            partial: 'bg-amber-100 text-amber-700',
            planned: 'bg-blue-100 text-blue-700',
            reject: 'bg-red-100 text-red-700',
            rejected: 'bg-red-100 text-red-700',
            defer: 'bg-amber-100 text-amber-700',
            deferred: 'bg-amber-100 text-amber-700',
            transfer: 'bg-purple-100 text-purple-700',
        }[d?.toLowerCase()] || 'bg-slate-100 text-slate-500');

        // ── Security controls grouped by dreaded event ──
        const groups = {};
        Object.values(plan).forEach(entry => {
            if (!entry.controls || entry.controls.length === 0) return;
            // Resolve name: prefer stored name, then look up from dreadedEventMap, then fall back
            const resolvedName = entry.dreadedEventName
                || (entry.dreadedEventId && dreadedEventMap[entry.dreadedEventId]?.name)
                || entry.dreadedEventId
                || 'Événement redouté';
            if (!groups[resolvedName]) groups[resolvedName] = [];
            entry.controls.forEach(ctrl => {
                groups[resolvedName].push({
                    name: ctrl.name || ctrl.controlId || ctrl.id || '—',
                    responsiblePerson: ctrl.responsiblePerson || '—',
                    decision: ctrl.decision || '—',
                    decisionDate: ctrl.decisionDate,
                    measureType: ctrl.measure_type || ctrl.category || '',
                    description: ctrl.description || ''
                });
            });
        });
        const groupEntries = Object.entries(groups);
        const totalControls = groupEntries.reduce((s, [, cs]) => s + cs.length, 0);

        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-8"
            >
                {/* ── Mesures de traitement du risque — groupées par scénario ── */}
                <div>
                    <div className="flex items-baseline justify-between pb-1.5 mb-4 border-b border-slate-200 dark:border-slate-700">
                        <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300">{t('versionManagement.snapshot.treatment.title')}</h4>
                        <span className="text-xs font-bold text-slate-400 tabular-nums">{totalMeasures}</span>
                    </div>

                    {scenarioMeasureGroups.length > 0 ? (
                        <div className="space-y-5">
                            {scenarioMeasureGroups.map((group, gi) => (
                                <div key={gi}>
                                    {/* Scenario name as sub-header */}
                                    <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2 pl-0.5">
                                        {group.scenarioName}
                                    </div>
                                    <div className="space-y-2">
                                        {group.measures.map((m, mi) => {
                                            const typeBadge = {
                                                ER: { label: 'ER', cls: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300' },
                                                ECO: { label: 'Éco.', cls: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300' },
                                                CUSTOM: { label: 'Personnalisé', cls: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300' },
                                            }[m._type] || { label: m._type, cls: 'bg-slate-100 text-slate-500' };

                                            return <MeasureCard key={mi} measure={m} borderColor="border-l-slate-300 dark:border-l-slate-600" typeBadge={typeBadge} />;
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-slate-400 italic text-xs pl-1">{t('versionManagement.snapshot.treatment.empty')}</p>
                    )}
                </div>

                {/* ── Contrôles de sécurité (Atelier 1) ── */}
                <SectionBlock
                    title={t('versionManagement.snapshot.controls.title')}
                    count={totalControls}
                    colorText="text-violet-700 dark:text-violet-400"
                    colorBorder="border-violet-200 dark:border-violet-800"
                    empty={t('versionManagement.snapshot.controls.empty')}
                >
                    {groupEntries.map(([evtName, controls], gi) => (
                        <div key={gi} className="rounded-lg border border-violet-100 dark:border-violet-900/50 overflow-hidden">
                            {/* Group header */}
                            <div className="bg-violet-50 dark:bg-violet-900/20 px-3 py-1.5 flex items-center justify-between">
                                <span className="text-xs font-semibold text-violet-800 dark:text-violet-300 truncate">{evtName}</span>
                                <span className="text-[10px] font-bold bg-violet-100 dark:bg-violet-800/50 text-violet-600 dark:text-violet-300 px-1.5 py-0.5 rounded shrink-0 ml-2">
                                    {controls.length} contrôle{controls.length > 1 ? 's' : ''}
                                </span>
                            </div>
                            {/* Controls table */}
                            <table className="w-full text-[11px]">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-violet-100 dark:border-violet-900/30">
                                        <th className="text-left px-3 py-1 font-semibold text-slate-500 w-3/5">{t('versionManagement.snapshot.controls.table.control')}</th>
                                        <th className="text-left px-2 py-1 font-semibold text-slate-500 w-1/5">{t('versionManagement.snapshot.controls.table.responsible')}</th>
                                        <th className="text-center px-2 py-1 font-semibold text-slate-500 w-1/5">{t('versionManagement.snapshot.controls.table.decision')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {controls.map((ctrl, ci) => (
                                        <tr key={ci} className={`border-b border-violet-50 dark:border-violet-900/20 ${ci % 2 === 0 ? 'bg-white dark:bg-slate-900/20' : 'bg-violet-50/30 dark:bg-violet-900/10'}`}>
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-medium text-slate-800 dark:text-slate-200 leading-snug">{ctrl.name}</div>
                                                {ctrl.measureType && <div className="text-[10px] text-slate-400 mt-0.5">{ctrl.measureType}</div>}
                                            </td>
                                            <td className="px-2 py-2 align-top text-slate-600 dark:text-slate-400">
                                                {ctrl.responsiblePerson !== '—' ? ctrl.responsiblePerson : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="px-2 py-2 align-top text-center">
                                                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${decisionBadge(ctrl.decision)}`}>
                                                    {ctrl.decision !== '—' ? ctrl.decision : 'N/D'}
                                                </span>
                                                {ctrl.decisionDate && (
                                                    <div className="text-[9px] text-slate-400 mt-0.5">
                                                        {new Date(ctrl.decisionDate).toLocaleDateString('fr-FR')}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </SectionBlock>

                {/* ── Plan d'Assurance Sécurité ── */}
                {allSelectedRulesList.length > 0 && (
                    <SectionBlock
                        title={t('versionManagement.snapshot.pas.title')}
                        count={allSelectedRulesList.length}
                        colorText="text-sky-700 dark:text-sky-400"
                        colorBorder="border-sky-200 dark:border-sky-800"
                        empty=""
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {allSelectedRulesList.map((rule, i) => {
                                const ruleId = rule.id || rule.originalId;
                                const ruleDetails = getRuleDetails(ruleId);
                                const ruleName = ruleDetails.name !== ruleId
                                    ? ruleDetails.name
                                    : (ruleDetails.code && ruleDetails.code !== ruleId ? ruleDetails.code : ruleId);

                                const planItems = Object.keys(plan)
                                    .filter(k => k.startsWith(`${ruleId}_`) || k === ruleId)
                                    .map(k => plan[k]);

                                const status = planItems.find(it => it.status === 'applied')?.status
                                    || planItems.find(it => it.status === 'planned')?.status
                                    || planItems[0]?.status;

                                return (
                                    <div key={i} className="p-2.5 bg-sky-50/50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-900 rounded-lg flex items-start gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-xs text-sky-800 dark:text-sky-300 leading-snug">
                                                {ruleName}
                                                {ruleDetails.frameworkName && ruleDetails.frameworkName !== 'Unknown' &&
                                                    <span className="text-sky-400 font-normal ml-1 text-[10px]">— {ruleDetails.frameworkName}</span>}
                                            </div>
                                            {ruleDetails.description &&
                                                <div className="text-[10px] text-slate-400 italic line-clamp-2 mt-0.5">{ruleDetails.description}</div>}
                                        </div>
                                        {status && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0 mt-0.5 ${status === 'applied' ? 'bg-green-100 text-green-700' : status === 'planned' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {status}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </SectionBlock>
                )}

                {totalMeasures === 0 && allSelectedRulesList.length === 0 && totalControls === 0 && (
                    <div className="text-center text-slate-400 italic py-8">
                        {t('versionManagement.snapshot.noMeasures')}<br />
                        <span className="text-xs">{t('versionManagement.snapshot.createSnapshotHint')}</span>
                    </div>
                )}
            </motion.div>
        );
    };


    const AlertTriangle = ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
    );

    return ReactDOM.createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm md:pr-[400px] lg:pr-[448px]">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-950">
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                                    Version {version?.version}
                                </h2>
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded uppercase">
                                    {t('versionManagement.snapshot.readonly')}
                                </span>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                                {version?.justification} • {new Date(version?.updatedAt).toLocaleDateString()}
                            </p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                            <X className="w-5 h-5 text-slate-500" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-hidden flex">
                        {loading ? (
                            <div className="flex-1 flex items-center justify-center">
                                <Loader className="w-8 h-8 animate-spin text-blue-500" />
                            </div>
                        ) : snapshot ? (
                            <>
                                {/* Sidebar Tabs */}
                                <div className="w-64 bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 p-4 space-y-2">
                                    {[
                                        { id: 'context', label: t('versionManagement.snapshot.tabs.context'), icon: FileText },
                                        { id: 'socle', label: t('versionManagement.snapshot.tabs.socle'), icon: Layers },
                                        { id: 'sources', label: t('versionManagement.snapshot.tabs.sources'), icon: Search },
                                        { id: 'scenarios', label: t('versionManagement.snapshot.tabs.scenarios'), icon: Target },
                                        { id: 'controls', label: t('versionManagement.snapshot.tabs.controls'), icon: ShieldCheck },
                                        { id: 'treatment', label: t('versionManagement.snapshot.tabs.treatment'), icon: ClipboardCheck },
                                    ].map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                                                ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 shadow-sm'
                                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                }`}
                                        >
                                            <tab.icon className="w-4 h-4" />
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Content Area */}
                                <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-slate-950">
                                    {activeTab === 'context' && renderContext()}
                                    {activeTab === 'socle' && renderSocle()}
                                    {activeTab === 'sources' && renderSources()}
                                    {activeTab === 'scenarios' && renderScenarios()}
                                    {activeTab === 'controls' && renderControls()}
                                    {activeTab === 'treatment' && renderTreatment()}
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-red-500">
                                {t('versionManagement.snapshot.errorLoading')}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
};

export default VersionSnapshotModal;
